import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  Skeleton,
  type FlexProps,
} from "@chakra-ui/react";
import { Calendar, LogIn, Menu as MenuIcon } from "lucide-react";
import { NavbarCTAButton } from "../Button";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import useScrollPosition from "../../hooks/useScrollPosition";
import StyledNavLink from "./StyledNavLink";
import { ServicesDropdown, AccountMenu } from "./NavDesktopMenu";
import { NavDrawer } from "./NavDrawer";

// How far the user must scroll before the white navbar background appears
// when overlaid on the hero image.
const SCROLL_THRESHOLD = 120;
const DESKTOP_NAV_MIN_WIDTH = 1100;
const NAV_MIN_GAP = 24;
const NAV_REEXPAND_BUFFER = 100;

interface HomeownerNavBarProps extends FlexProps {
  heroMode?: boolean;
}

export default function HomeownerNavBar({
  heroMode = false,
  ...props
}: HomeownerNavBarProps) {
  const { role, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const { scrollPosition } = useScrollPosition();

  // Content-aware nav: measure actual overlap between left group and right CTAs,
  // collapse to hamburger when gap < 24px, re-expand with 100px hysteresis.
  const innerFlexRef = useRef<HTMLDivElement>(null);
  const leftGroupRef = useRef<HTMLDivElement>(null);
  const rightGroupRef = useRef<HTMLDivElement>(null);
  const isDesktopRef = useRef(
    typeof window === "undefined" ? true : window.innerWidth >= DESKTOP_NAV_MIN_WIDTH
  );
  const collapsedAtRef = useRef<number | null>(null);
  const [showDesktopNav, setShowDesktopNav] = useState(isDesktopRef.current);

  useEffect(() => {
    let rafId: number | null = null;

    function check() {
      if (!innerFlexRef.current) return;
      const viewportWidth =
        typeof window === "undefined" ? DESKTOP_NAV_MIN_WIDTH : window.innerWidth;
      const w = innerFlexRef.current.offsetWidth || viewportWidth;

      if (isDesktopRef.current) {
        if (!leftGroupRef.current || !rightGroupRef.current) return;
        const leftRect = leftGroupRef.current.getBoundingClientRect();
        const rightRect = rightGroupRef.current.getBoundingClientRect();
        if (leftRect.width === 0 || rightRect.width === 0) return;
        const gap =
          rightRect.left -
          leftRect.right;
        if (gap < NAV_MIN_GAP) {
          isDesktopRef.current = false;
          collapsedAtRef.current = w;
          setShowDesktopNav(false);
        }
      } else if (
        (collapsedAtRef.current !== null &&
          w > collapsedAtRef.current + NAV_REEXPAND_BUFFER) ||
        (collapsedAtRef.current === null && viewportWidth >= DESKTOP_NAV_MIN_WIDTH)
      ) {
        isDesktopRef.current = true;
        collapsedAtRef.current = null;
        setShowDesktopNav(true);
        scheduleCheck();
      }
    }

    function scheduleCheck() {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        check();
      });
    }

    const ro = new ResizeObserver(check);
    if (innerFlexRef.current) ro.observe(innerFlexRef.current);
    window.addEventListener("resize", scheduleCheck);
    check();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", scheduleCheck);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // In heroMode the white bg is hidden until the user scrolls past the threshold.
  // On all other pages the bg is always visible.
  const scrolled = !heroMode || scrollPosition > SCROLL_THRESHOLD;

  if (loading) {
    return (
      <NavbarSkeleton heroMode={heroMode} scrolled={scrolled} {...props} />
    );
  }

  const isAuthenticated = role === "homeowner";

  return (
    <Flex
      w="full"
      justifyContent="center"
      alignItems="center"
      bg="transparent"
      zIndex={20}
      position="sticky"
      top={0}
      {...props}
    >
      {/* Sliding background overlay — scales in from the top once scrolled */}
      <Box
        position="absolute"
        inset={0}
        bg="rgba(255,255,255,0.97)"
        borderBottom="1px solid"
        borderColor="blackAlpha.100"
        boxShadow="0 8px 24px rgba(15, 23, 42, 0.06)"
        style={{
          transform: scrolled ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top",
          transition: scrolled
            ? "transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)"
            : "transform 0.22s ease-in",
          zIndex: 0,
        }}
      />

      <Flex
        w="full"
        maxW="1512px"
        px={{ base: "1.25rem", tablet: "2rem", desktop: "6.25rem" }}
        py="0.65rem"
        alignItems="center"
        justifyContent="space-between"
        gap="1rem"
        position="relative"
        zIndex={1}
        ref={innerFlexRef}
      >
        {/* Left group: Logo + nav links */}
        <HStack gap={[null, null, "2rem", "2.75rem"]} alignItems="center" ref={leftGroupRef as never}>
          {/* Cross-fade between white logo (hero) and colour logo (scrolled) */}
          <Box
            position="relative"
            height={{ base: "1.85rem", desktop: "2rem" }}
            flexShrink={0}
            cursor="pointer"
            onClick={() => navigate("/")}
            transition="transform 0.22s ease, opacity 0.22s ease"
            _hover={{ transform: "translateY(-1px)", opacity: 0.82 }}
            _active={{ transform: "scale(0.97)" }}
          >
            <Image
              position="absolute"
              top={0}
              left={0}
              width="auto"
              height="100%"
              src="/home/hhLogo-white.png"
              style={{
                opacity: scrolled ? 0 : 1,
                transition: "opacity 0.35s ease",
              }}
            />
            <Image
              position="absolute"
              top={0}
              left={0}
              width="auto"
              height="100%"
              src="/home/hhLogo2.png"
              style={{
                opacity: scrolled ? 1 : 0,
                transition: "opacity 0.35s ease",
              }}
            />
            {/* Invisible copy to reserve natural width so the container doesn't collapse */}
            <Image
              width="auto"
              height="100%"
              src="/home/hhLogo2.png"
              visibility="hidden"
              aria-hidden
            />
          </Box>

          {/* Desktop nav links: Home · Our Services · Packages · FAQ · Book Now */}
          <HStack
            as="nav"
            gap="2.5rem"
            alignItems="center"
            display={showDesktopNav ? "flex" : "none"}
          >
            <StyledNavLink to="/" heroMode={heroMode}>Home</StyledNavLink>
            <ServicesDropdown isActive={path.startsWith("/services")} heroMode={heroMode} />
            <StyledNavLink to="/packages" heroMode={heroMode}>Packages</StyledNavLink>
            <StyledNavLink to="/faq" heroMode={heroMode}>FAQ</StyledNavLink>
            <NavbarCTAButton
              scrolled={scrolled}
              onClick={() => navigate("/dashboard")}
              icon={<Calendar size={14} />}
            >
              Book Now
            </NavbarCTAButton>
          </HStack>
        </HStack>

        {/* Mobile hamburger */}
        <NavDrawer isAuthenticated={isAuthenticated} heroMode={heroMode} visible={!showDesktopNav} />

        {/* Desktop CTAs: Account / Sign In */}
        <HStack
          gap="2rem"
          alignItems="center"
          display={showDesktopNav ? "flex" : "none"}
          ref={rightGroupRef as never}
        >
          {isAuthenticated ? (
            <AccountMenu heroMode={heroMode} />
          ) : (
            <NavbarCTAButton
              scrolled={scrolled}
              onClick={() => navigate("/login")}
              icon={<LogIn size={14} />}
            >
              Sign In
            </NavbarCTAButton>
          )}
        </HStack>
      </Flex>
    </Flex>
  );
}

function NavbarSkeleton({
  heroMode = false,
  scrolled = true,
  ...props
}: FlexProps & { heroMode?: boolean; scrolled?: boolean }) {
  void heroMode;

  return (
    <Flex
      w="full"
      justifyContent="center"
      alignItems="center"
      bg="transparent"
      zIndex={20}
      position="sticky"
      top={0}
      {...props}
    >
      <Box
        position="absolute"
        inset={0}
        bg="rgba(255,255,255,0.92)"
        backdropFilter="blur(12px)"
        borderBottom="1px solid"
        borderColor="blackAlpha.100"
        boxShadow="0 8px 24px rgba(15, 23, 42, 0.06)"
        style={{
          transform: scrolled ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top",
          transition: scrolled
            ? "transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)"
            : "transform 0.22s ease-in",
          zIndex: 0,
        }}
      />

      <Flex
        w="full"
        maxW="1512px"
        px={{ base: "1.25rem", tablet: "2rem", desktop: "6.25rem" }}
        py="0.65rem"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        zIndex={1}
      >
        <Box
          position="relative"
          height={{ base: "1.85rem", desktop: "2rem" }}
          flexShrink={0}
        >
          <Image
            position="absolute"
            top={0}
            left={0}
            width="auto"
            height="100%"
            src="/home/hhLogo-white.png"
            style={{
              opacity: scrolled ? 0 : 1,
              transition: "opacity 0.35s ease",
            }}
          />
          <Image
            position="absolute"
            top={0}
            left={0}
            width="auto"
            height="100%"
            src="/home/hhLogo2.png"
            style={{
              opacity: scrolled ? 1 : 0,
              transition: "opacity 0.35s ease",
            }}
          />
          <Image
            width="auto"
            height="100%"
            src="/home/hhLogo2.png"
            visibility="hidden"
            aria-hidden
          />
        </Box>

        <HStack gap="1.5rem" display={["none", null, null, "flex"]}>
          <Skeleton height="1rem" width="3.5rem" rounded="full" />
          <Skeleton height="1rem" width="6rem" rounded="full" />
          <Skeleton height="1rem" width="4rem" rounded="full" />
          <Skeleton height="1rem" width="2.5rem" rounded="full" />
        </HStack>

        <HStack gap="0.75rem" display={["none", null, null, "flex"]}>
          <Skeleton height="2.75rem" width="8.5rem" rounded="full" />
          <Skeleton height="2.75rem" width="2.75rem" rounded="full" />
        </HStack>

        <IconButton
          display={["flex", null, null, "none"]}
          rounded="full"
          variant="outline"
          aria-label="menu"
        >
          <MenuIcon />
        </IconButton>
      </Flex>
    </Flex>
  );
}
